import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { baseAPI } from '../../../config';
import { TASK_TYPE_LABELS } from '@claimup/shared/constants/assignments';
import AIAgentPromptForm from './AIAgentPromptForm';
import AIAgentPromptHistory from './AIAgentPromptHistory';
import { companyLabel } from '../../../utils/companyLabel';

// Los prompts que no son de una tarea no tienen `task_type`: sin esto la fila
// salía con el nombre en blanco.
const KIND_LABELS = { summary: 'Resumen del expediente', consult: 'Consultar el caso' };
const nombreDePrompt = (p) =>
  TASK_TYPE_LABELS[p.task_type] ||
  p.task_type ||
  `${KIND_LABELS[p.kind] || p.kind}${p.variant ? ` (${p.variant})` : ''}${
    p.kind === 'consult' ? (p.business_line === 'account-receivable' ? ' · AR' : ' · recobro') : ''
  }`;

const AIAgentPrompts = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [prompts, setPrompts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [companies, setCompanies] = useState([]);
  const [filterTaskType, setFilterTaskType] = useState('');
  const [filterActive, setFilterActive] = useState('true');
  const [filterCompany, setFilterCompany] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [editingPrompt, setEditingPrompt] = useState(null);
  // Empresa preseleccionada al crear (deep-link desde el profile de una company).
  const [createCompanyId, setCreateCompanyId] = useState(null);
  const [historyPromptId, setHistoryPromptId] = useState(null);
  // Para auto-abrir un prompt/creación una sola vez desde la query.
  const deepLinkHandled = useRef(false);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchPrompts = useCallback(async () => {
    try {
      const params = {};
      if (filterTaskType) params.task_type = filterTaskType;
      if (filterActive !== 'all') params.active = filterActive;
      if (filterCompany) params.client_id = filterCompany;

      const response = await axios.get(`${baseAPI}/ai-system-prompts`, { params, headers });
      setPrompts(response.data);
    } catch (error) {
      console.error('Error fetching prompts:', error);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterTaskType, filterActive, filterCompany]);

  useEffect(() => {
    fetchPrompts();
  }, [fetchPrompts]);

  // Companies para el selector/filtro y para mostrar el nombre en la tabla.
  useEffect(() => {
    axios
      .get(`${baseAPI}/users/list?role=company`, { headers })
      .then(res => setCompanies(res.data || []))
      .catch(() => setCompanies([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const companyNameById = useMemo(() => {
    const map = {};
    companies.forEach(c => { map[c._id] = companyLabel(c); });
    return map;
  }, [companies]);

  const handleDeactivate = async (id) => {
    if (!window.confirm('¿Desactivar este prompt?')) return;
    try {
      await axios.delete(`${baseAPI}/ai-system-prompts/${id}`, { headers });
      fetchPrompts();
    } catch (error) {
      console.error('Error deactivating prompt:', error);
    }
  };

  // «Esta versión es la que vale»: la enciende y apaga las demás del mismo
  // prompt. Sin esto, poner en uso una versión obligaba a inventarse otra por
  // encima, porque el agente coge la MÁS ALTA de entre las activas.
  const handleActivate = async (prompt) => {
    const nombre = nombreDePrompt(prompt);
    if (!window.confirm(`¿Usar la v${prompt.version} de «${nombre}»? Se desactivarán las demás versiones.`)) return;
    try {
      await axios.post(`${baseAPI}/ai-system-prompts/${prompt._id}/activate`, {}, { headers });
      fetchPrompts();
    } catch (error) {
      console.error('Error activating prompt:', error);
    }
  };

  const handleEdit = (prompt) => {
    setCreateCompanyId(null);
    setEditingPrompt(prompt);
    setFormOpen(true);
  };

  const handleCreate = (companyId = null) => {
    setEditingPrompt(null);
    setCreateCompanyId(companyId);
    setFormOpen(true);
  };

  const handleFormClose = () => {
    setFormOpen(false);
    setEditingPrompt(null);
    setCreateCompanyId(null);
    fetchPrompts();
  };

  // Deep-link desde el profile de una company: abrir el prompt indicado
  // (promptId) para editar, o el form de creación con la empresa preseleccionada
  // (newCompanyId). Se ejecuta una vez; el prompt se busca por id (no depende del
  // filtro del listado). Al terminar limpia los params para no reabrir en remount.
  useEffect(() => {
    if (deepLinkHandled.current) return;
    const promptId = searchParams.get('promptId');
    const newCompanyId = searchParams.get('newCompanyId');
    if (!promptId && !newCompanyId) return;
    deepLinkHandled.current = true;

    const clearDeepLink = () => {
      const next = new URLSearchParams(searchParams);
      next.delete('promptId');
      next.delete('newCompanyId');
      setSearchParams(next, { replace: true });
    };

    if (promptId) {
      axios
        .get(`${baseAPI}/ai-system-prompts/${promptId}`, { headers })
        .then(res => { if (res.data) handleEdit(res.data); })
        .catch(() => {})
        .finally(clearDeepLink);
    } else {
      handleCreate(newCompanyId);
      clearDeepLink();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formatTimeAgo = timestamp => {
    if (!timestamp) return 'Nunca';
    const diff = Date.now() - new Date(timestamp).getTime();
    const minutes = Math.floor(diff / 60000);
    if (minutes < 1) return 'Hace un momento';
    if (minutes < 60) return `Hace ${minutes} min`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `Hace ${hours}h`;
    return `Hace ${Math.floor(hours / 24)}d`;
  };

  return (
    <div>
      {/* Filters + Create button */}
      <div className='flex flex-wrap items-center gap-3 mb-4'>
        <select
          value={filterTaskType}
          onChange={e => setFilterTaskType(e.target.value)}
          className='text-sm border border-slate-200 rounded-lg px-3 py-1.5'
        >
          <option value=''>Todos los tipos</option>
          {Object.entries(TASK_TYPE_LABELS).map(([value, label]) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </select>

        <select
          value={filterActive}
          onChange={e => setFilterActive(e.target.value)}
          className='text-sm border border-slate-200 rounded-lg px-3 py-1.5'
        >
          <option value='all'>Todos los estados</option>
          <option value='true'>Activos</option>
          <option value='false'>Inactivos</option>
        </select>

        <select
          value={filterCompany}
          onChange={e => setFilterCompany(e.target.value)}
          className='text-sm border border-slate-200 rounded-lg px-3 py-1.5'
        >
          <option value=''>Todas las empresas</option>
          {companies.map(c => (
            <option key={c._id} value={c._id}>{companyLabel(c)}</option>
          ))}
        </select>

        <div className='flex-grow'></div>

        <button
          onClick={() => handleCreate()}
          className='text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg px-4 py-1.5'
        >
          Nuevo prompt
        </button>
      </div>

      {/* Table */}
      {loading ? (
        <div className='text-sm text-slate-500'>Cargando prompts...</div>
      ) : prompts.length === 0 ? (
        <div className='text-center py-8 text-sm text-slate-400'>No hay prompts configurados</div>
      ) : (
        <div className='rounded-lg border border-slate-200 overflow-hidden'>
          <table className='w-full text-sm'>
            <thead>
              <tr className='bg-slate-50 border-b border-slate-200'>
                <th className='text-left px-4 py-2 text-xs font-semibold uppercase text-slate-500'>Tipo</th>
                <th className='text-left px-4 py-2 text-xs font-semibold uppercase text-slate-500'>Empresa</th>
                <th className='text-left px-4 py-2 text-xs font-semibold uppercase text-slate-500'>Descripcion</th>
                <th className='text-left px-4 py-2 text-xs font-semibold uppercase text-slate-500'>Provider / Modelo</th>
                <th className='text-center px-4 py-2 text-xs font-semibold uppercase text-slate-500'>Version</th>
                <th className='text-center px-4 py-2 text-xs font-semibold uppercase text-slate-500'>Estado</th>
                <th className='text-center px-4 py-2 text-xs font-semibold uppercase text-slate-500'>Tools</th>
                <th className='text-left px-4 py-2 text-xs font-semibold uppercase text-slate-500'>Ultima vez</th>
                <th className='text-right px-4 py-2 text-xs font-semibold uppercase text-slate-500'>Acciones</th>
              </tr>
            </thead>
            <tbody className='divide-y divide-slate-200'>
              {prompts.map(prompt => (
                <tr key={prompt._id} className='hover:bg-slate-50'>
                  <td className='px-4 py-2 font-medium text-slate-700'>
                    {nombreDePrompt(prompt)}
                  </td>
                  <td className='px-4 py-2'>
                    {prompt.client_id ? (
                      <span className='inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-indigo-100 text-indigo-700'>
                        {companyNameById[prompt.client_id] || 'Empresa'}
                      </span>
                    ) : (
                      <span className='text-xs text-slate-400'>Genérico</span>
                    )}
                  </td>
                  <td className='px-4 py-2 text-slate-600 max-w-xs truncate'>{prompt.description || '-'}</td>
                  <td className='px-4 py-2 text-slate-600'>
                    <span className='text-xs'>{prompt.ai_provider}</span> / <span className='text-xs font-medium'>{prompt.model_id}</span>
                  </td>
                  <td className='px-4 py-2 text-center text-slate-600'>v{prompt.version}</td>
                  <td className='px-4 py-2 text-center'>
                    {/* «Activo» no basta: de entre los activos, el agente usa
                        el de versión más alta. Los demás están encendidos y no
                        se usan nunca, y esa diferencia es la que se cobraba
                        cara al desactivar el que mandaba. */}
                    <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${
                      prompt.in_use
                        ? 'bg-green-100 text-green-700'
                        : prompt.active
                          ? 'bg-amber-100 text-amber-700'
                          : 'bg-slate-100 text-slate-500'
                    }`}>
                      {prompt.in_use ? 'En uso' : prompt.active ? 'Activo, sin usar' : 'Inactivo'}
                    </span>
                  </td>
                  <td className='px-4 py-2 text-center text-slate-600 text-xs'>
                    {prompt.tools_enabled ? `${prompt.tools_enabled.length} tools` : 'Todas'}
                  </td>
                  <td className='px-4 py-2 text-xs text-slate-400'>{formatTimeAgo(prompt.last_used_at)}</td>
                  <td className='px-4 py-2 text-right'>
                    <div className='flex justify-end gap-2'>
                      <button
                        onClick={() => handleEdit(prompt)}
                        className='text-xs text-blue-600 hover:text-blue-800 font-medium'
                      >
                        Editar
                      </button>
                      {!prompt.in_use && (
                        <button
                          onClick={() => handleActivate(prompt)}
                          className='text-xs text-green-600 hover:text-green-800 font-medium'
                        >
                          Usar esta
                        </button>
                      )}
                      {prompt.active && (
                        <button
                          onClick={() => handleDeactivate(prompt._id)}
                          className='text-xs text-slate-500 hover:text-slate-700 font-medium'
                        >
                          Desactivar
                        </button>
                      )}
                      {prompt.version > 1 && (
                        <button
                          onClick={() => setHistoryPromptId(prompt._id)}
                          className='text-xs text-orange-600 hover:text-orange-800 font-medium'
                        >
                          Historial
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Form modal */}
      <AIAgentPromptForm
        prompt={editingPrompt}
        companies={companies}
        defaultCompanyId={createCompanyId}
        modalOpen={formOpen}
        onClose={handleFormClose}
      />

      {/* History modal */}
      <AIAgentPromptHistory
        promptId={historyPromptId}
        modalOpen={!!historyPromptId}
        onClose={() => setHistoryPromptId(null)}
        onReverted={fetchPrompts}
      />
    </div>
  );
};

export default AIAgentPrompts;
